import { Box, MenuItem, Select, useTheme, useMediaQuery } from "@mui/material";
import { LabelText } from "../Typography/AppTypography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "../Button/IconButton";

/**
 * Common Incremental Pagination Component
 */

const IncrementalPagination = ({
  page,
  pageSize,
  dataLength,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
  isLastPage,
  sx = {},
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isFirstPage = Number(page) === 0;

  // Server-side incremental pagination
  // If API returns less than pageSize, no more pages
  const isLastPageComputed = isLastPage !== undefined ? isLastPage : dataLength < pageSize;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: { xs: 1, sm: 2 },
        m: { xs: 1.5, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        minWidth: 0,
        ...sx,
      }}
    >
      {/* Rows per page */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        <LabelText noWrap>
          {isSmallScreen ? "Rows:" : "Rows per page:"}
        </LabelText>
        <Select
          size="small"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          sx={{
            minWidth: { xs: 56, sm: 64 },
            height: { xs: 32, sm: 36 },
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
            "& .MuiSelect-select": { py: { xs: 0.5, sm: 1 } },
          }}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Pagination controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
        <IconButton
          variant={isFirstPage ? "disabled" : "primary"}
          size="small"
          icon={<ChevronLeftIcon />}
          // onClick={() => onPageChange(page - 1, isFirstPage)}
          onClick={() => {
            if (!isFirstPage) onPageChange(page - 1);
          }}
          isNotAllowed={isFirstPage}
          tooltip="Previous page"
          disabled={isFirstPage}
        />

        {/* Current Page */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            minHeight: 32,
            maxHeight: 32,
            borderRadius: 1.25,
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            minWidth: 32,
            textAlign: "center",
            fontWeight: 600,
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
            py: 0,
            lineHeight: 1,
            flexShrink: 0,
            boxSizing: "border-box",
          }}
        >
          {page + 1}
        </Box>

        <IconButton
          variant={isLastPageComputed ? "disabled" : "primary"}
          size="small"
          icon={<ChevronRightIcon />}
          // onClick={() => onPageChange(page + 1, isLastPageComputed)}
          onClick={() => {
            if (!isLastPageComputed) onPageChange(page + 1);
          }}
          isNotAllowed={isLastPageComputed}
          tooltip="Next page"
          disabled={isLastPageComputed}
        />
      </Box>
    </Box>
  );
};


export default IncrementalPagination;
